<template>
  <div class="image-slider-container">
    <!-- Loading state -->
    <div v-if="!slides || slides.length === 0" class="text-center py-5">
      Loading slides...
    </div>
    
    <!-- Slider container -->
    <div v-else class="fade-slider" @mouseenter="pauseSlider" @mouseleave="resumeSlider">
      <!-- Indicators -->
      <div class="slider-dots" v-if="showDots">
        <button
          v-for="(slide, index) in slides"
          :key="'dot-'+index"
          :class="{ active: currentIndex === index }"
          @click="goToSlide(index)"
          :aria-label="'Go to slide ' + (index + 1)"
        ></button>
      </div>
      
      <!-- Slides with pure fade transition -->
      <div class="slider-track">
        <div 
          v-for="(slide, index) in slides" 
          :key="'slide-'+index"
          class="slide-item"
          :class="{ active: currentIndex === index }"
        >
          <img 
            :src="'/images/albums/'+slide.id_album+'/'+slide.imagen" 
            :alt="slide.alt || 'Slide image'" 
            class="slide-image"
          >
          
          <!-- Text content with separate animations -->
          <div class="slide-content">
            <transition name="fade-up-delay">
              <p v-if="currentIndex === index" class="subtitle">{{ slide.imagen_descripcion }}</p>
            </transition>
            <transition name="fade-up">
              <h3 v-if="currentIndex === index" class="title">{{ slide.imagen_titulo }}</h3>
            </transition>
          </div>
        </div>
      </div>
      
      <!-- Navigation arrows -->
      <button class="slider-prev" @click="prevSlide" aria-label="Previous slide">
        <span>&#10094;</span>
      </button>
      <button class="slider-next" @click="nextSlide" aria-label="Next slide">
        <span>&#10095;</span>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';

export default {
  name: 'AnimatedFadeImageSlider',
  props: {
    slides: {
      type: Array,
      required: true,
      default: () => [],
      validator: (value) => {
        if (!value || value.length === 0) return true;
        return value.every(slide => {
          return slide.id_album && slide.imagen;
        });
      }
    },
    interval: {
      type: Number,
      default: 5000
    },
    autoPlay: {
      type: Boolean,
      default: true
    },
    fadeDuration: {
      type: Number,
      default: 1000
    },
    showDots: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const currentIndex = ref(0);
    let intervalId = null;

    const nextSlide = () => {
      if (!props.slides || props.slides.length === 0) return;
      currentIndex.value = (currentIndex.value + 1) % props.slides.length;
    };

    const prevSlide = () => {
      if (!props.slides || props.slides.length === 0) return;
      currentIndex.value = (currentIndex.value - 1 + props.slides.length) % props.slides.length;
    };

    const goToSlide = (index) => {
      if (!props.slides || props.slides.length === 0 || index === currentIndex.value) return;
      if (index >= 0 && index < props.slides.length) {
        currentIndex.value = index;
      }
    };

    const startAutoPlay = () => {
      console.log('startAutoPlay')
      if (props.autoPlay && props.slides && props.slides.length > 1) {
        console.log('startAutoPlay true')
        intervalId = setInterval(nextSlide, props.interval);
      }
    };

    const pauseSlider = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const resumeSlider = () => {
      if (props.autoPlay && !intervalId && props.slides && props.slides.length > 1) {
        startAutoPlay();
      }
    };

    onMounted(() => {
      if (props.slides && props.slides.length > 0) {
        startAutoPlay();
      }
    });

    onUnmounted(() => {
      pauseSlider();
    });

    return {
      currentIndex,
      nextSlide,
      prevSlide,
      goToSlide,
      pauseSlider,
      resumeSlider
    };
  }
};
</script>

<style scoped>
.image-slider-container {
  position: relative;
  width: 100%;
  margin: 0 auto;
}

.fade-slider {
  position: relative;
  width: 100%;
  height: 100%;
}

.slider-track {
  position: relative;
  width: 100%;
  height: 100%;
}

.slide-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity v-bind('fadeDuration + "ms"') ease;
}

.slide-item.active {
  opacity: 1;
  z-index: 1;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center; /* Focus on center of image */
}

.slide-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  width: 80%;
  color: white;
}

.title {
  font-size: 4rem;
  font-weight: bold;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.subtitle {
  font-size: 1.5rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

/* Text Animations */
.fade-up-enter-active {
  transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: 0.3s;
}
.fade-up-leave-active {
  transition: all 0.5s cubic-bezier(0.5, 0, 0.75, 0);
}
.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.fade-up-delay-enter-active {
  transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: 0.6s;
}
.fade-up-delay-leave-active {
  transition: all 0.5s cubic-bezier(0.5, 0, 0.75, 0);
}
.fade-up-delay-enter-from,
.fade-up-delay-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Navigation styles remain the same */
.slider-dots {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
  z-index: 2;
}

.slider-dots button {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.slider-dots button.active {
  background: white;
  transform: scale(1.2);
}

.slider-prev,
.slider-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.3);
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 2;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slider-prev:hover,
.slider-next:hover {
  background: rgba(0, 0, 0, 0.6);
}

.slider-prev {
  left: 20px;
  padding-bottom: 9px;
  padding-right: 8px;
}

.slider-next {
  right: 20px;
  padding-bottom: 9px;
  padding-left: 8px;
}

@media (max-width: 768px) {
  .fade-slider {
    height: 130%;
  }
  
  .title {
    font-size: 2rem;
  }
  
  .subtitle {
    font-size: 1.2rem;
  }
  
  .slide-content {
    width: 90%;
    padding: 1rem;
  }
  
  .slider-prev,
  .slider-next {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
}

@media (max-width: 1024px) {
  .fade-slider {
    height: 130%;
  }
  
  .title {
    font-size: 2rem;
  }
  
  .subtitle {
    font-size: 1.2rem;
  }
}
</style>